import React, { useState, useEffect, useMemo } from "react";
import { toast } from 'react-toastify';
import { useMyContext } from '../Provider/CommonProvider';
import MessageBox from './MessageBox';
import AdminCustomButton from './AdminCustomButton';
import AdminCustomInput from './AdminCustomInput';
import { IoMdSearch, IoMdClose, IoMdCloudDownload, IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FaFileVideo, FaFileImage, FaFileAlt, FaExternalLinkAlt, FaFolder } from "react-icons/fa";

export default function S3SyncModal({ open, onClose, upload_type, onSuccess }) {
    const { fetchRequest } = useMyContext();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [storageInfo, setStorageInfo] = useState({ bucket: '', directory: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPrefix, setCurrentPrefix] = useState('');
    const [historyList, setHistoryList] = useState([]);
    const [basePath, setBasePath] = useState('');

    const baseURL = process.env.REACT_APP_Baseurl;
    const fetchApi = `${baseURL}/admin/library/s3-files`;
    const syncApi = `${baseURL}/admin/library/s3-sync`;

    useEffect(() => {
        if (open) {
            fetchS3Files();
            setSelectedFiles([]);
            setSearchTerm('');
            setHistoryList([]);
        }
    }, [open]);

    const fetchS3Files = (prefix = undefined) => {
        setLoading(true);
        fetchRequest({
            method: 'GET',
            url: fetchApi,
            params: { upload_type, prefix }
        })
        .then(response => {
            if (response.status) {
                setFiles(response.data || []);
                setStorageInfo({ bucket: response.bucket, directory: response.currentPrefix });
                if (prefix === undefined && response.currentPrefix !== undefined) {
                    setCurrentPrefix(response.currentPrefix);
                    setBasePath(response.basePath);
                } else if (prefix !== undefined) {
                    setCurrentPrefix(prefix);
                }
            }
        })
        .catch(error => {
            toast.error(error?.response?.data?.message || 'Error fetching S3 files');
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const handleSelect = (key) => {
        if (selectedFiles.includes(key)) {
            setSelectedFiles(prev => prev.filter(k => k !== key));
        } else {
            setSelectedFiles(prev => [...prev, key]);
        }
    };

    const handleSelectAll = (isCheck) => {
        const selectableFiles = filteredFiles.filter(f => f.type === 'file');
        if (isCheck) {
            setSelectedFiles(selectableFiles.map(f => f.key));
        } else {
            setSelectedFiles([]);
        }
    };

    const handleSync = () => {
        if (!selectedFiles.length) {
            toast.warning('Please select at least one file to sync');
            return;
        }

        setSyncing(true);
        fetchRequest({
            method: 'POST',
            url: syncApi,
            data: {
                files: selectedFiles,
                upload_type: upload_type,
                clone_to: undefined, 
                action_type: 'created'
            }
        })
        .then(response => {
            if (response.status) {
                toast.success('Files successfully synchronized from S3!');
                if (onSuccess) onSuccess();
                onClose();
            }
        })
        .catch(error => {
            toast.error(error?.response?.data?.message || 'Error syncing S3 files');
        })
        .finally(() => {
            setSyncing(false);
        });
    };

    const filteredFiles = useMemo(() => {
        if (!searchTerm) return files;
        return files.filter(file => 
            file.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [files, searchTerm]);

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (['mp4', 'mkv', 'webm', 'mov'].includes(ext)) return <FaFileVideo className="text-primary fs-4" />;
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <FaFileImage className="text-success fs-4" />;
        return <FaFileAlt className="text-secondary fs-4" />;
    };

    if (!open) return null;

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                <div className="modal-content modern-modal border-0 shadow-lg theme-bg-color theme-text-color overflow-hidden" style={{ borderRadius: '1.25rem' }}>
                    
                    <div className="modal-header border-0 p-4 pb-0 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="modal-title fw-bold mb-1 d-flex align-items-center">
                                <IoMdCloudDownload className="me-2 text-primary" />
                                AWS S3 Sync
                            </h4>
                            <p className="text-muted small mb-0 fw-normal">
                                {upload_type} repository: <span className="text-primary">{storageInfo.bucket}</span>/{storageInfo.directory}
                            </p>
                        </div>
                        <button type="button" className="btn-close shadow-none p-3 m-0" onClick={onClose}></button>
                    </div>

                    <div className="px-4 pt-4">
                        {historyList.length > 0 && (
                            <div className="mb-3">
                                <button className="btn btn-sm btn-outline-secondary d-flex align-items-center rounded-3 shadow-none"
                                    onClick={() => {
                                        const newHistory = [...historyList];
                                        const prevPrefix = newHistory.pop();
                                        setHistoryList(newHistory);
                                        fetchS3Files(prevPrefix);
                                    }}
                                >
                                    <i className="fa fa-arrow-left me-2"></i> Back
                                </button>
                            </div>
                        )}
                        <div className="row g-3">
                            <div className="col-12 col-md-8">
                                <div className="position-relative">
                                    <AdminCustomInput 
                                        placeHolder="Search files by name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        noMargin={true}
                                        className="ps-5 py-2.5 rounded-4"
                                    />
                                    <IoMdSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted fs-5" />
                                </div>
                            </div>
                            <div className="col-12 col-md-4">
                                {(() => {
                                    const selectableFiles = filteredFiles.filter(f => f.type === 'file');
                                    const isAllSelected = selectableFiles.length > 0 && selectedFiles.length === selectableFiles.length;
                                    
                                    return (
                                    <button 
                                        type="button"
                                        className={`w-100 h-100 p-2.5 border rounded-4 d-flex align-items-center justify-content-center transition-all shadow-none ${isAllSelected ? 'bg-primary border-primary text-white' : 'theme-border-color-secondary theme-bg-color-secondary theme-text-color'}`}
                                        onClick={() => handleSelectAll(!isAllSelected)}
                                        disabled={selectableFiles.length === 0}
                                    >
                                        <IoMdCheckmarkCircleOutline className={`me-2 fs-5 ${isAllSelected ? 'text-white' : 'text-primary'}`} />
                                        <span className="fw-bold">
                                            {isAllSelected ? 'Deselect All' : 'Select All'} ({selectableFiles.length})
                                        </span>
                                    </button>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    <div className="modal-body p-4 pt-3">
                        {loading ? (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                                <span className="text-muted fw-medium">Scanning S3 Bucket...</span>
                            </div>
                        ) : filteredFiles.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                                    <IoMdClose className="text-muted fs-1" />
                                </div>
                                <h5 className="fw-bold mb-2">No Files Found</h5>
                                <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '300px' }}>
                                    {searchTerm ? `No files match your search "${searchTerm}"` : "We couldn't find any files in the specified S3 directory."}
                                </p>
                                <AdminCustomButton 
                                    saveMethod={fetchS3Files}
                                    saveText="Refresh Library"
                                    className="px-4 rounded-4"
                                />
                            </div>
                        ) : (
                            <div className="s3-file-list custom-scrollbar pe-1" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                                {filteredFiles.map((file, idx) => (
                                    <div 
                                        key={idx} 
                                        className={`d-flex align-items-center mb-3 p-3 border rounded-4 transition-all cursor-pointer shadow-sm-hover ${file.type === 'file' && selectedFiles.includes(file.key) ? 'border-primary theme-bg-color-secondary' : 'theme-border-color-secondary'}`}
                                        onClick={() => {
                                            if (file.type === 'folder') {
                                                setHistoryList(prev => [...prev, currentPrefix]);
                                                fetchS3Files(file.fullPath);
                                            } else {
                                                handleSelect(file.key);
                                            }
                                        }}
                                    >
                                        {file.type === 'file' ? (
                                        <div className="form-check mb-0">
                                            <input 
                                                type="checkbox"
                                                id={`s3file-${idx}`}
                                                className="form-check-input mt-0 shadow-none cursor-pointer"
                                                style={{ width: '1.1rem', height: '1.1rem' }}
                                                checked={selectedFiles.includes(file.key)}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleSelect(file.key);
                                                }}
                                            />
                                        </div>
                                        ) : (
                                        <div className="form-check mb-0" style={{ width: '1.1rem', height: '1.1rem' }}>
                                            {/* Spacer for alignment */}
                                        </div>
                                        )}
                                        
                                        <div className="ms-3 me-3 d-none d-sm-flex align-items-center justify-content-center bg-light rounded-3" style={{ width: '45px', height: '45px', flexShrink: 0 }}>
                                            {file.type === 'folder' ? <FaFolder className="text-warning fs-4" /> : getFileIcon(file.name)}
                                        </div>

                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-bold text-truncate" title={file.name}>{file.name}</div>
                                            {file.type === 'file' && (
                                            <div className="text-muted x-small mt-1 d-flex gap-3 flex-wrap align-items-center">
                                                <span className="d-flex align-items-center">
                                                    <i className="far fa-hdd me-1"></i>
                                                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                                                </span>
                                                <span className="d-flex align-items-center">
                                                    <i className="far fa-calendar-alt me-1"></i>
                                                    {new Date(file.lastModified).toLocaleDateString()}
                                                </span>
                                            </div>
                                            )}
                                        </div>

                                        <div className="ms-auto ps-3 d-flex align-items-center">
                                            {file.type === 'file' && selectedFiles.includes(file.key) && (
                                                <IoMdCheckmarkCircleOutline className="text-primary fs-4" />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer border-0 p-4 bg-light-subtle d-flex flex-column flex-sm-row gap-3">
                        <div className="flex-grow-1 text-center text-sm-start">
                            {selectedFiles.length > 0 && (
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill fw-medium">
                                    {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected for sync
                                </span>
                            )}
                        </div>
                        <div className="d-flex gap-2 w-100 w-sm-auto justify-content-center">
                            <button 
                                type="button" 
                                className="btn btn-link text-muted text-decoration-none fw-semibold px-4 py-2 shadow-none border-0" 
                                onClick={onClose} 
                                disabled={syncing}
                            >
                                Cancel
                            </button>
                            <AdminCustomButton 
                                saveMethod={handleSync}
                                saveText={syncing ? "Synchronizing..." : "Sync Selected Files"}
                                loading={syncing}
                                disabled={selectedFiles.length === 0 || loading}
                                className="px-5 rounded-4 shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>
            
            <style jsx>{`
                .transition-all { transition: all 0.2s ease-in-out; }
                .cursor-pointer { cursor: pointer; }
                .shadow-sm-hover:hover { transform: translateY(-2px); box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important; }
                .hover-primary:hover { color: #0d6efd !important; background: #e7f1ff !important; }
                .x-small { font-size: 0.75rem; }
                .py-2.5 { padding-top: 0.625rem; padding-bottom: 0.625rem; }
                .p-2.5 { padding: 0.625rem; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #ced4da; }
            `}</style>
        </div>
    );
}

